// @core/stores/affiliate/AffiliateReferralLinkStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { affiliateApi } from '@core/api';
import type {
  CreateReferralLinkRequest,
  ReferralLink,
} from '@core/types/affiliate';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AffiliateReferralLinkStore {
  // List state
  items: ReferralLink[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: ReferralLink | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, ReferralLink>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): ReferralLink | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: ReferralLink | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create referral link
   */
  async CreateReferralLink(data: CreateReferralLinkRequest): Promise<ReferralLink> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.CreateReferralLink(data);
      const item = response.link;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * List referral links
   */
  async ListReferralLinks(params?: PaginatedRequest & { affiliate_id?: string | number | boolean; campaign_id?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await affiliateApi.ListReferralLinks(params);

      runInAction(() => {
        this.items = response.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const affiliateReferralLinkStore = new AffiliateReferralLinkStore();
